/**
 * DynamicConfigSelector Component
 *
 * D3 migration: uses catalog-native ModelSelect + ImageConfigPanel instead of
 * the legacy playground ModelSelector + /v1/images/models-driven dropdowns.
 * web_search toggle removed (supports_web_search is dead data, always false).
 */

import { Select } from '@/components/ui/Select';
import { ModelSelect } from '@/components/model-display/ModelSelect';
import { ImageConfigPanel } from '@/components/model-display/ImageConfigPanel';
import { useCatalogModels } from '@/lib/queries/useCatalogModels';
import { cn } from '@/lib/cn';
import type { ImageGenerationConfig } from '@/types/playground';
import type { ImageCapability } from '@/types';
import { useI18n } from '@/i18n';

interface DynamicConfigSelectorProps {
  config: ImageGenerationConfig;
  operationMode: 'generate' | 'edit' | 'fuse';
  onModelChange: (modelId: string) => void;
  onSizeChange: (size: string) => void;
  onAspectRatioChange: (ratio: string) => void;
  onQualityChange: (quality: string | null) => void;
  onBackgroundChange?: (background: string | null) => void;
  compact?: boolean;
  className?: string;
}

export function DynamicConfigSelector({
  config,
  operationMode,
  onModelChange,
  onSizeChange,
  onAspectRatioChange,
  onQualityChange,
  onBackgroundChange,
  compact = false,
  className,
}: DynamicConfigSelectorProps) {
  const { t } = useI18n();
  const { data: catalogModels = [] } = useCatalogModels('image');

  const selectedCatalogModel = catalogModels.find((m) => m.id === config.model);
  const imageCap =
    selectedCatalogModel?.capability.modality === 'image'
      ? (selectedCatalogModel.capability as ImageCapability)
      : null;

  const handleConfigChange = (patch: {
    size?: string;
    aspect_ratio?: string;
    quality?: string;
  }) => {
    if (patch.size !== undefined) onSizeChange(patch.size);
    if (patch.aspect_ratio !== undefined) onAspectRatioChange(patch.aspect_ratio);
    if (patch.quality !== undefined) onQualityChange(patch.quality || null);
  };

  const backgroundSelect = imageCap?.supports_transparency && onBackgroundChange ? (
    <Select
      label={t('playground.labels.background')}
      value={config.background || ''}
      onChange={(e) => onBackgroundChange(e.target.value || null)}
      options={[
        { value: '', label: t('playground.labels.defaultOption') },
        { value: 'transparent', label: t('playground.labels.transparent') },
        { value: 'white', label: t('playground.labels.white') },
        { value: 'auto', label: t('playground.labels.auto') },
      ]}
    />
  ) : null;

  if (compact) {
    return (
      <div className={cn('flex flex-wrap gap-3 items-start', className)}>
        <div className="min-w-[160px]">
          <ModelSelect
            value={config.model}
            onChange={onModelChange}
            modality="image"
            operationFilter={operationMode}
            variant="compact"
          />
        </div>

        {selectedCatalogModel && (
          <div className="flex-1 min-w-[160px]">
            <ImageConfigPanel
              model={selectedCatalogModel}
              value={{
                size: config.size || null,
                aspect_ratio: config.aspect_ratio || null,
                quality: config.quality,
              }}
              onChange={handleConfigChange}
            />
          </div>
        )}

        {backgroundSelect && <div className="w-full sm:w-auto">{backgroundSelect}</div>}
      </div>
    );
  }

  return (
    <div className={cn('space-y-5', className)}>
      <ModelSelect
        value={config.model}
        onChange={onModelChange}
        modality="image"
        operationFilter={operationMode}
      />

      {selectedCatalogModel && (
        <ImageConfigPanel
          model={selectedCatalogModel}
          value={{
            size: config.size || null,
            aspect_ratio: config.aspect_ratio || null,
            quality: config.quality,
          }}
          onChange={handleConfigChange}
        />
      )}

      {backgroundSelect}

      {imageCap && (
        <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
          {imageCap.max_images_out > 1 && (
            <span className={cn('px-2 py-1 bg-muted rounded-md')}>
              {t('playground.labels.maxGenerate').replace(
                '{count}',
                String(imageCap.max_images_out),
              )}
            </span>
          )}
          {operationMode === 'fuse' && imageCap.max_images_fuse > 0 && (
            <span className={cn('px-2 py-1 bg-muted rounded-md')}>
              {t('playground.labels.maxFuse').replace(
                '{count}',
                String(imageCap.max_images_fuse),
              )}
            </span>
          )}
          {imageCap.output_formats.length > 1 && (
            <span className={cn('px-2 py-1 bg-muted rounded-md')}>
              {t('playground.labels.output')}: {imageCap.output_formats.join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
